import {
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  forwardRef,
  AnchorHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mildang/mui';
import useCustomRouter from 'hooks/useCustomRouter';

interface NextLinkComposedProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<NextLinkProps, 'href' | 'as' | 'onClick' | 'onMouseEnter' | 'onTouchStart'> {
  to: NextLinkProps['href'];
  linkAs?: NextLinkProps['as'];
  href?: NextLinkProps['href'];
  isExternal?: boolean;
}

// eslint-disable-next-line react/display-name
export const NextLinkComposed = forwardRef<HTMLAnchorElement, NextLinkComposedProps>((props, ref) => {
  const {
    to,
    linkAs,

    href,
    replace,
    scroll,
    passHref,
    shallow,
    prefetch,
    locale,
    isExternal,
    ...other
  } = props;

  return (
    <NextLink
      href={to}
      prefetch={prefetch}
      as={linkAs}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      passHref={passHref}
      locale={locale}
      ref={ref}
      {...other}
    ></NextLink>
  );
});

export type LinkProps = {
  activeClassName?: string;
  as?: NextLinkProps['as'];
  href: NextLinkProps['href'];
  noLinkStyle?: boolean;
  parentPush?: boolean;
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> &
  Omit<MuiLinkProps, 'href'>;

// A styled version of the Next.js Link component:
// https://nextjs.org/docs/#with-link
const LinkForwardRef: ForwardRefExoticComponent<
  PropsWithoutRef<LinkProps> & RefAttributes<HTMLAnchorElement>
  // eslint-disable-next-line react/display-name
> = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    activeClassName = 'active',
    as: linkAs,
    className: classNameProps,
    href,
    noLinkStyle,
    parentPush,
    role, // Link don't have roles.
    isExternal: isExternalProp,
    ...other
  } = props;

  const router = useCustomRouter();
  const pathname = typeof href === 'string' ? href : href.pathname;

  const className = clsx(classNameProps, {
    [activeClassName]: router?.pathname === pathname && activeClassName,
  });

  const isExternal =
    (typeof href === 'string' && (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0)) ||
    isExternalProp;

  if (isExternal) {
    if (noLinkStyle) {
      return <a className={className} href={href as string} ref={ref as any} {...other} />;
    }

    return <MuiLink className={className} href={href as string} ref={ref} {...other} />;
  }

  if (noLinkStyle) {
    return <NextLinkComposed className={className} ref={ref as any} to={href} {...other} />;
  }

  return (
    <MuiLink
      component={NextLinkComposed}
      linkAs={linkAs}
      className={className}
      ref={ref}
      to={href}
      {...other}
      onClick={(e) => {
        if (parentPush) {
          router.pushParentUrl();
        } else {
          router.setParentUrlFromPrevUrl();
        }
        other?.onClick?.(e);
      }}
    />
  );
});

export default LinkForwardRef;
